// Force-keep ranges handed to the semantic compressor alongside the text it is
// compressing. A token-classification compressor drops the tokens it scores as
// low-information, which for prose is fine but is destructive for the exact
// substrings a developer will later grep, copy, or paste: identifiers, paths,
// numbers, error codes, and anything the author already fenced in backticks.
//
// Worked examples (the shapes each pass is meant to catch):
//   "Returns the `userId` for a givenAccount, or throws ENOENT."
//     -> `userId` (backticks), givenAccount (camelCase), ENOENT (error code)
//   "See src/db/database.ts:42 — retries 3 times before failing."
//     -> src/db/database.ts:42 (path + line), 3 (number)
//   "just some ordinary prose here"
//     -> nothing protected; the whole thing is fair game

export interface ProtectedRange {
  start: number;
  end: number;
}

/**
 * Anything inside backticks — single or triple. Non-greedy, and allowed to span
 * newlines so fenced blocks inside a docstring survive whole.
 */
const BACKTICKED = /`{1,3}[\s\S]*?`{1,3}/g;

/**
 * Path-shaped: a slash-separated path ending in a file extension, optionally
 * followed by `:line` or `:line:col`. Also catches bare `file.ext:12`.
 */
const PATH_LIKE = /\b[\w.-]+(?:\/[\w.-]+)*\.[A-Za-z]\w{0,9}(?::\d+){0,2}\b/g;

/** SCREAMING_SNAKE / error-code shaped: ENOENT, E_BADF, HTTP_404. */
const SHOUTY = /\b[A-Z][A-Z0-9_]{2,}\b/g;

/**
 * Identifier-shaped: camelCase, PascalCase, snake_case, or a dotted/`::`
 * member chain. Deliberately does NOT match a lone lowercase word — that's
 * ordinary prose, and protecting it would make the compressor a no-op.
 */
const IDENTIFIER_LIKE =
  /\b(?:[a-z]+(?:[A-Z][a-z\d]*)+|[A-Z][a-z\d]+(?:[A-Z][a-z\d]*)+|\w+_\w+|\w+(?:(?:\.|::)\w+)+)\b/g;

/** Numbers, including decimals, hex, and unit suffixes (`200ms`, `0x1F`, `1.5`). */
const NUMERIC = /\b(?:0[xX][0-9a-fA-F]+|\d+(?:\.\d+)?[a-zA-Z%]{0,4})\b/g;

const PASSES = [BACKTICKED, PATH_LIKE, SHOUTY, IDENTIFIER_LIKE, NUMERIC];

/**
 * Collects the force-keep ranges for `text`, merged and sorted by `start`.
 * Offsets are into `text` itself, matching CompressRequest.protectedRanges.
 */
export function protectedRangesFor(text: string): ProtectedRange[] {
  const found: ProtectedRange[] = [];
  for (const pattern of PASSES) {
    // Each pass owns its lastIndex; these are module-level /g regexes reused
    // across calls, so reset before iterating rather than trusting the last run.
    pattern.lastIndex = 0;
    for (let m = pattern.exec(text); m !== null; m = pattern.exec(text)) {
      if (m[0].length === 0) {
        pattern.lastIndex++; // defensive: never spin on a zero-width match
        continue;
      }
      found.push({ start: m.index, end: m.index + m[0].length });
    }
  }
  return mergeRanges(found);
}

/** Merges overlapping/adjacent ranges so the compressor gets a clean, sorted list. */
export function mergeRanges(ranges: ProtectedRange[]): ProtectedRange[] {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: ProtectedRange[] = [{ ...sorted[0] }];
  for (const range of sorted.slice(1)) {
    const last = merged[merged.length - 1];
    if (range.start <= last.end) last.end = Math.max(last.end, range.end);
    else merged.push({ ...range });
  }
  return merged;
}
